import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { getDomains, generateQuiz, submitQuiz, saveQuizAnswer } from '../api';
import { Brain, CheckCircle2, XCircle, Award, Home, RotateCcw, Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from '../hooks/use-toast';

// Simple UUID generator
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const QuizMode = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const domainParam = searchParams.get('domain');

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(domainParam || null);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizSessionId, setQuizSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [quizComplete, setQuizComplete] = useState(false);
  const [startTime, setStartTime] = useState(null);

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    if (domainParam && domains.length > 0) {
      startQuiz(domainParam);
    }
  }, [domainParam, domains]);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      const domainsData = await getDomains();
      setDomains(domainsData);
    } catch (error) {
      console.error('Error fetching domains:', error);
      toast({
        title: 'Error loading domains',
        description: 'Failed to load quiz domains. Please refresh.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = async (domainId) => {
    try {
      setGenerating(true);
      setSelectedDomain(domainId);
      
      // Create new quiz session ID
      const sessionId = generateUUID();
      setQuizSessionId(sessionId);
      
      toast({
        title: 'Generating Quiz...',
        description: 'AI is creating questions for you. This may take a moment.',
      });

      // Generate quiz questions using AI
      const result = await generateQuiz(domainId, 5);
      
      setQuestions(result.questions);
      setQuizStarted(true);
      setStartTime(new Date().toISOString());
      
      toast({
        title: 'Quiz Ready!',
        description: `Generated ${result.questions.length} questions`,
      });
    } catch (error) {
      console.error('Error generating quiz:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate quiz. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleAnswerSelect = (answerIndex) => {
    if (showExplanation) return;
    setSelectedAnswer(answerIndex);
  };

  const handleSubmitAnswer = async () => {
    if (selectedAnswer === null) {
      toast({
        title: 'Please select an answer',
        variant: 'destructive'
      });
      return;
    }

    const currentQuestion = questions[currentQuestionIndex];
    const isCorrect = selectedAnswer === currentQuestion.correct_answer;

    const answer = {
      question_id: currentQuestion.id,
      selected_answer: selectedAnswer,
      is_correct: isCorrect
    };

    // Auto-save answer to backend
    try {
      await saveQuizAnswer(quizSessionId, currentQuestion.id, selectedAnswer, isCorrect);
    } catch (error) {
      console.error('Error saving answer:', error);
      // Continue even if save fails
    }

    setAnswers([...answers, answer]);
    setShowExplanation(true);
  };

  const handleNextQuestion = async () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      // Quiz complete - submit results
      try {
        const endTime = new Date().toISOString();
        const allAnswers = [...answers, {
          question_id: questions[currentQuestionIndex].id,
          selected_answer: selectedAnswer,
          is_correct: selectedAnswer === questions[currentQuestionIndex].correct_answer
        }];

        const result = await submitQuiz(
          selectedDomain,
          quizSessionId,
          allAnswers,
          startTime,
          endTime
        );
        
        toast({
          title: 'Quiz Complete!',
          description: `Score: ${result.score}% (${result.correct_answers}/${result.total_questions} correct)`,
        });
        
        setQuizComplete(true);
      } catch (error) {
        console.error('Error submitting quiz:', error);
        toast({
          title: 'Error',
          description: 'Failed to save quiz results.',
          variant: 'destructive'
        });
        setQuizComplete(true);
      }
    }
  };

  const resetQuiz = () => {
    setQuizStarted(false);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setAnswers([]);
    setQuizComplete(false);
    setSelectedDomain(null);
    setStartTime(null);
  };

  // Domain Selection Screen
  if (!quizStarted) {
    if (loading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
        </div>
      );
    }

    if (generating) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto" />
              <p className="text-lg font-medium">Generating Quiz Questions...</p>
              <p className="text-sm text-slate-600">AI is creating personalized questions for you</p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 pb-20">
        <div className="max-w-4xl mx-auto p-6 space-y-6 mb-20">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900">Practice Quiz</h1>
              <p className="text-lg text-slate-600 mt-1">Test your knowledge with AI-generated questions</p>
            </div>
            <Button onClick={() => navigate('/')} variant="outline">
              <Home className="mr-2 h-4 w-4" />
              Home
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Select a Domain</CardTitle>
              <CardDescription>Choose which area you'd like to be quizzed on</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              {domains.map(domain => (
                <Button
                  key={domain.id}
                  onClick={() => startQuiz(domain.id)}
                  variant="outline"
                  className="h-auto p-6 justify-start hover:shadow-lg transition-all duration-200"
                >
                  <div className="flex items-center gap-4 w-full">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: domain.color }}
                    />
                    <div className="text-left flex-1">
                      <div className="font-semibold text-lg">{domain.name}</div>
                      <div className="text-sm text-slate-500">{domain.items.length} items</div>
                    </div>
                    <Brain className="h-6 w-6 text-slate-400" />
                  </div>
                </Button>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const domain = domains.find(d => d.id === selectedDomain);
  const isCorrect = showExplanation && selectedAnswer === currentQuestion.correct_answer;

  // Quiz Complete Screen
  if (quizComplete) {
    const correctCount = answers.filter(a => a.is_correct).length;
    const score = Math.round((correctCount / questions.length) * 100);

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 pb-20">
        <div className="max-w-4xl mx-auto p-6 space-y-6 mb-20">
          <Card className="border-t-4 border-t-blue-500">
            <CardHeader>
              <CardTitle className="text-3xl flex items-center gap-2">
                <Award className="h-8 w-8 text-blue-600" />
                Quiz Complete!
              </CardTitle>
              <CardDescription>Here's how you did</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <div className="text-5xl font-bold text-blue-600">{score}%</div>
                  <div className="text-slate-600 mt-2">Score</div>
                </div>
                <div className="text-center p-6 bg-green-50 rounded-lg">
                  <div className="text-5xl font-bold text-green-600">{correctCount}/{questions.length}</div>
                  <div className="text-slate-600 mt-2">Correct</div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-lg">Review Your Answers</h3>
                {questions.map((q, idx) => {
                  const answer = answers[idx];
                  return (
                    <div key={q.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {answer?.is_correct ? (
                          <CheckCircle2 className="h-6 w-6 text-green-600" />
                        ) : (
                          <XCircle className="h-6 w-6 text-red-600" />
                        )}
                        <div>
                          <Badge variant="outline" className="mr-2">{q.task_code}</Badge>
                          <span className="text-sm text-slate-600">Question {idx + 1}</span>
                        </div>
                      </div>
                      <span className="text-sm font-semibold" style={{ color: answer?.is_correct ? '#10B981' : '#EF4444' }}>
                        {answer?.is_correct ? 'Correct' : 'Incorrect'}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="space-y-2 pt-4">
                <Button onClick={() => navigate('/')} className="w-full" size="lg">
                  <Home className="mr-2 h-5 w-5" />
                  Back to Dashboard
                </Button>
                <Button onClick={resetQuiz} variant="outline" className="w-full" size="lg">
                  <RotateCcw className="mr-2 h-5 w-5" />
                  Take Another Quiz
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Quiz Question Screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 pb-20">
      <div className="max-w-4xl mx-auto p-6 space-y-6 mb-20">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Practice Quiz</h1>
            <p className="text-slate-600 mt-1">
              Question {currentQuestionIndex + 1} of {questions.length}
            </p>
          </div>
          <Button onClick={() => navigate('/')} variant="outline">
            <Home className="mr-2 h-4 w-4" />
            Home
          </Button>
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <Progress value={((currentQuestionIndex + 1) / questions.length) * 100} className="h-3" />
          <div className="flex justify-between text-sm text-slate-500">
            <span>{domain?.name}</span>
            <span>{Math.round(((currentQuestionIndex + 1) / questions.length) * 100)}%</span>
          </div>
        </div>

        {/* Question Card */}
        <Card className="border-t-4" style={{ borderTopColor: domain?.color }}>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Badge variant="outline" style={{ borderColor: domain?.color, color: domain?.color }}>
                {currentQuestion.taskCode}
              </Badge>
              <span className="text-sm text-slate-600">{domain?.name}</span>
            </div>
            <CardTitle className="text-xl mt-4 leading-relaxed">
              {currentQuestion.question}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <RadioGroup 
              key={currentQuestionIndex} 
              value={selectedAnswer !== null ? selectedAnswer.toString() : undefined} 
              onValueChange={(val) => handleAnswerSelect(parseInt(val))}
            >
              <div className="space-y-3">
                {currentQuestion.options.map((option, idx) => {
                  const isSelected = selectedAnswer === idx;
                  const isCorrectAnswer = idx === currentQuestion.correct_answer;
                  const showCorrect = showExplanation && isCorrectAnswer;
                  const showWrong = showExplanation && isSelected && !isCorrectAnswer;

                  return (
                    <div
                      key={idx}
                      className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-all duration-200 ${
                        showCorrect ? 'border-green-500 bg-green-50' :
                        showWrong ? 'border-red-500 bg-red-50' :
                        isSelected ? 'border-blue-500 bg-blue-50' :
                        'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <RadioGroupItem value={idx.toString()} id={`option-${idx}`} disabled={showExplanation} />
                      <Label 
                        htmlFor={`option-${idx}`} 
                        className="flex-1 cursor-pointer text-base leading-relaxed"
                      >
                        {option}
                      </Label>
                      {showCorrect && <CheckCircle2 className="h-6 w-6 text-green-600" />}
                      {showWrong && <XCircle className="h-6 w-6 text-red-600" />}
                    </div>
                  );
                })}
              </div>
            </RadioGroup>

            {showExplanation && (
              <div className={`p-4 rounded-lg ${
                isCorrect ? 'bg-green-50 border-2 border-green-200' : 'bg-orange-50 border-2 border-orange-200'
              }`}>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  {isCorrect ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <Brain className="h-5 w-5 text-orange-600" />
                  )}
                  {isCorrect ? 'Correct!' : 'Not quite'}
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {currentQuestion.explanation}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              {!showExplanation ? (
                <Button 
                  onClick={handleSubmitAnswer} 
                  className="w-full" 
                  size="lg"
                  style={{ backgroundColor: domain?.color }}
                  disabled={selectedAnswer === null}
                >
                  Submit Answer
                </Button>
              ) : (
                <Button 
                  onClick={handleNextQuestion} 
                  className="w-full" 
                  size="lg"
                  style={{ backgroundColor: domain?.color }}
                >
                  {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Finish Quiz'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QuizMode;
